import { motion } from 'framer-motion';
import { Activity, Bug, Coins, Info, Leaf, TreePalm } from 'lucide-react';
import { useAuth } from '../../hooks/use-auth';
import { useEffect, useState } from 'react';
import { LoadingSpinner } from '../../components/loading/LoadingSpinner';
import { MetaTags } from '../../components/seo/MetaTags';
import {
  getDiseaseHistory,
  getUserHistory,
  getUserStats
} from '../../lib/firestore';
import { getUserCredits } from '../../lib/credits';
import { PlantHistoryItem } from '../../components/plant/PlantHistoryItem';
import { PlantHistoryModal } from '../../components/plant/PlantHistoryModal';
import { Link } from 'react-router-dom';
import { DiseaseHistoryModal } from '../../components/disease/DiseaseHistoryModal';
import { DiseaseHistoryItem } from '../../components/disease/DiseaseHistoryItem';
import { useToast } from '../../hooks/use-toast';

const tooltips = {
  'Total Identifications':
    'Total number of plants you have identified using our service',
  'Disease Detections': 'Number of plant disease scans performed',
  'Credit Balance':
    'Your current credit balance. Credits are used for plant identifications and disease detection',
  'Regular Credits':
    'Free monthly credits that reset at the beginning of each month',
  'Premium Credits': 'Purchased credits that never expire'
};

function StatCard({ title, value, icon: Icon, color = 'emerald' }) {
  const [showTooltip, setShowTooltip] = useState(false);

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-white rounded-xl shadow-lg p-6 relative group"
    >
      <div className="flex items-start justify-between">
        <div>
          <div className="flex items-center gap-2">
            <div className={`bg-${color}-100 p-3 rounded-lg`}>
              <Icon className={`w-6 h-6 text-${color}-600`} />
            </div>
            <p className="text-gray-500 text-sm">{title}</p>
            <div
              className="relative"
              onMouseEnter={() => setShowTooltip(true)}
              onMouseLeave={() => setShowTooltip(false)}
            >
              <Info className="w-4 h-4 text-gray-400 hover:text-gray-600 cursor-help" />
              {showTooltip && (
                <div className="absolute left-1/2 -translate-x-1/2 bottom-full mb-2 w-64 p-3 bg-gray-900 text-white text-xs rounded-lg shadow-lg z-50">
                  <p>{tooltips[title]}</p>
                  <div className="absolute left-1/2 -translate-x-1/2 top-full w-2 h-2 bg-gray-900 transform rotate-45" />
                </div>
              )}
            </div>
          </div>
          <h3 className="text-2xl font-bold text-gray-900">{value}</h3>
        </div>
      </div>
    </motion.div>
  );
}

export function Dashboard() {
  const { toast } = useToast();
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [history, setHistory] = useState([]);
  const [credits, setCredits] = useState({ regular: 0, premium: 0, total: 0 });
  const [isLoading, setIsLoading] = useState(true);
  const [selectedPlant, setSelectedPlant] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [diseaseHistory, setDiseaseHistory] = useState([]);
  const [selectedDisease, setSelectedDisease] = useState(null);
  const [isDiseaseModalOpen, setIsDiseaseModalOpen] = useState(false);

  const statsConfig = [
    {
      title: 'Total Identifications',
      value: stats => stats?.totalIdentifications || 0,
      icon: Leaf,
      color: 'emerald'
    },
    {
      title: 'Disease Detections',
      value: stats => stats?.totalDiseaseDetections || 0,
      icon: Bug,
      color: 'amber'
    },
    {
      title: 'Regular Credits',
      value: credits => credits.regular,
      icon: Coins,
      color: 'blue'
    },
    {
      title: 'Premium Credits',
      value: credits => credits.premium,
      icon: Coins,
      color: 'purple'
    }
  ];

  useEffect(() => {
    async function fetchData() {
      if (user) {
        try {
          const [userStats, userCredits, plantHistory, diseaseDetections] =
            await Promise.all([
              getUserStats(user.uid),
              getUserCredits(user.uid),
              getUserHistory(user.uid),
              getDiseaseHistory(user.uid)
            ]);
          setStats(userStats);
          setCredits(userCredits);
          setHistory(plantHistory);
          setDiseaseHistory(diseaseDetections);
        } catch (error) {
          console.error('Error fetching data:', error);
          toast({
            title: 'Error',
            description: 'Failed to load dashboard data',
            variant: 'destructive'
          });
        } finally {
          setIsLoading(false);
        }
      }
    }

    fetchData();
  }, [user, toast]);

  const handlePlantClick = plant => {
    setSelectedPlant(plant);
    setIsModalOpen(true);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen pt-24 px-4 flex items-center justify-center">
        <LoadingSpinner size="large" />
      </div>
    );
  }

  return (
    <>
      <MetaTags
        title="Dashboard"
        description="View your plant identification statistics and activity."
        noindex={true}
      />
      <div className="min-h-screen bg-gradient-to-b from-emerald-50/50 to-transparent pt-24 px-4 pb-32">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="mb-8"
          >
            <h1 className="text-3xl font-bold text-gray-900">Dashboard</h1>
            <p className="text-gray-600">
              Overview of your plant identification activity
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {statsConfig.map((stat, index) => (
              <StatCard
                key={stat.title}
                title={stat.title}
                value={stat.value(
                  stat.title.includes('Credits') ? credits : stats
                )}
                icon={stat.icon}
                color={stat.color}
              />
            ))}
          </div>

          {/* Recent Activity */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-xl shadow-lg p-6"
          >
            <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <TreePalm className="w-5 h-5 text-emerald-600" /> Recent plant
                Identifications
              </h2>
              {stats?.recentActivity?.length > 0 ? (
                <div className="space-y-4">
                  {history.slice(0, 3).map(item => (
                    <PlantHistoryItem
                      key={item.id}
                      item={item}
                      onClick={handlePlantClick}
                    />
                  ))}
                  {history.length > 3 && (
                    <Link
                      to="/identifier"
                      className="text-sm text-emerald-600 hover:text-emerald-700 font-medium block text-center mt-4"
                    >
                      View all identifications
                    </Link>
                  )}
                </div>
              ) : (
                <div className="text-center text-gray-600 py-8">
                  <TreePalm className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                  <p>No recent activity</p>
                  <p className="text-sm">Start by identifying some plants!</p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-xl shadow-lg p-6 mt-6">
              <h2 className="text-xl font-semibold mb-6 flex items-center gap-2">
                <Bug className="w-5 h-5 text-amber-600" />
                Recent Disease Detections
              </h2>
              <div className="space-y-4">
                {diseaseHistory.length === 0 ? (
                  <div className="text-center text-gray-600 py-8">
                    <Bug className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                    <p>No disease detections yet</p>
                    <Link
                      to="/disease-detection"
                      className="text-amber-600 hover:text-amber-700 font-medium mt-2 inline-block"
                    >
                      Start detecting diseases
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {diseaseHistory.map(detection => (
                      <DiseaseHistoryItem
                        key={detection.id}
                        detection={detection}
                        onClick={detection => {
                          setSelectedDisease(detection);
                          setIsDiseaseModalOpen(true);
                        }}
                      />
                    ))}
                  </div>
                )}
                {diseaseHistory.length > 3 && (
                  <Link
                    to="/disease-detection"
                    className="text-sm text-amber-600 hover:text-amber-700 font-medium block text-center mt-4"
                  >
                    View all detections
                  </Link>
                )}
              </div>
            </div>
          </motion.div>
        </div>

        {/* Plant History Modal */}
        <PlantHistoryModal
          isOpen={isModalOpen}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedPlant(null);
          }}
          plant={selectedPlant}
        />

        {/* Disease History Modal */}
        <DiseaseHistoryModal
          isOpen={isDiseaseModalOpen}
          onClose={() => setIsDiseaseModalOpen(false)}
          detection={selectedDisease}
        />
      </div>
    </>
  );
}
